/**
 * Big Cap Lab tab — dedicated alpha research on the large-cap segment.
 * Levels vs changes, options contrarian fade, vol conditioning,
 * walk-forward ML ablation, and regime x options (Track B).
 */
import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AlphaVal, PanelSummary, RunBar, SigBadge, StatCard, ToggleGroup } from "./common";

const FEATURE_LABELS = {
  sector_centrality: "Sector Centrality",
  global_centrality: "Global Centrality",
  dynamic_upside: "Dynamic Upside",
  target_age_days: "Target Age",
  grad_1m: "Nowcast Grad 1M",
  grad_3m: "Nowcast Grad 3M",
  confidence: "Nowcast Confidence",
  log_n_funds: "Fund Breadth",
  mom_63_sec: "3M Momentum (ctrl)",
  rev_21: "1M Reversal (ctrl)",
  d_sector_centrality: "Δ Sector Centrality",
  d_global_centrality: "Δ Global Centrality",
  grad_rev_1m: "Gradient Revision",
  breadth_chg_1m: "Δ Breadth 1M",
  breadth_chg_3m: "Δ Breadth 3M",
  target_rev_1m: "Target Revision",
  atm_iv: "ATM IV",
  iv_skew: "IV Skew",
  iv_spread: "IV Spread",
  risk_reversal: "Risk Reversal",
  cp_volume_ratio: "Call Vol Share",
  term_slope: "IV Term Slope",
  os_ratio: "O/S Ratio",
  vrp: "VRP",
};
const fl = (f) => FEATURE_LABELS[f] || f;

const SET_LABELS = {
  levels_controls: "Levels + Controls",
  plus_changes: "+ Changes (innovations)",
  plus_options: "+ Options",
  regime_baseline: "Regime baseline (clusters only)",
  regime_plus_options: "Regime + Options",
};

const Th = ({ children, right }) => (
  <th className={`py-1.5 px-2 font-medium text-muted-foreground ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);
const Td = ({ children, right }) => (
  <td className={`py-1.5 px-2 ${right ? "text-right" : ""}`}>{children}</td>
);

const IcCell = ({ ic }) =>
  ic ? (
    <span className="font-mono text-xs">
      {ic.mean_ic ?? "-"}{" "}
      <span className="text-muted-foreground">(t={ic.nw_tstat ?? "-"})</span>
    </span>
  ) : (
    <span className="text-muted-foreground">-</span>
  );

const SpreadCell = ({ sp }) =>
  sp ? (
    <span className="text-xs">
      <AlphaVal value={sp.mean_pct} />{" "}
      <span className="text-muted-foreground font-mono">(t={sp.nw_tstat ?? "-"})</span>
    </span>
  ) : (
    <span className="text-muted-foreground">-</span>
  );

/* ---------------- Levels vs Changes ---------------- */

const LevelsVsChanges = ({ block }) => {
  const feats = block?.features || {};
  const groups = [
    ["level", "Levels (static positioning)"],
    ["change", "Changes (positioning innovations)"],
  ];
  return (
    <Card data-testid="bigcap-levels-changes">
      <CardHeader>
        <CardTitle className="text-base">Levels vs Changes — do innovations beat static levels?</CardTitle>
        <CardDescription>
          Quintile sorts + Spearman ICs on big caps only, forward sector-relative alpha.
          Efficient large caps should price levels instantly — but react to <em>changes</em> in
          institutional positioning.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Feature</Th>
                <Th right>1M IC</Th>
                <Th right>3M IC</Th>
                <Th right>3M Q5–Q1</Th>
                <Th right>Sig</Th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([kind, label]) => (
                <React.Fragment key={kind}>
                  <tr className="bg-muted/40">
                    <td colSpan={5} className="py-1 px-2 text-xs font-semibold text-muted-foreground">
                      {label}
                    </td>
                  </tr>
                  {Object.entries(feats)
                    .filter(([, b]) => b.kind === kind)
                    .map(([feat, b]) => (
                      <tr key={feat} className="border-b border-border/50" data-testid={`bigcap-lvc-${feat}`}>
                        <Td>
                          <span className="font-medium">{b.label || fl(feat)}</span>
                          <div className="text-[11px] text-muted-foreground">{b.rationale}</div>
                        </Td>
                        <Td right><IcCell ic={b.sector_21?.ic} /></Td>
                        <Td right><IcCell ic={b.sector_63?.ic} /></Td>
                        <Td right><SpreadCell sp={b.sector_63?.spread} /></Td>
                        <Td right><SigBadge t={b.sector_63?.spread?.nw_tstat} /></Td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- ML Ablation ---------------- */

const MlAblation = ({ ml }) => {
  if (!ml?.results) return null;
  const proto = ml.protocol || {};
  const imp = Object.entries(ml.importance || {})
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, 10);
  return (
    <Card data-testid="bigcap-ml-ablation">
      <CardHeader>
        <CardTitle className="text-base">Walk-Forward ML Ablation — what adds big-cap alpha?</CardTitle>
        <CardDescription>
          Three nested GBT models refit monthly ({proto.walk_forward}). OOS{" "}
          {proto.oos_start} → {proto.oos_end}, {proto.oos_dates} dates,{" "}
          {proto.oos_observations?.toLocaleString()} observations. Paired per-date IC uplift is
          the honest test of each block's marginal value.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Feature Set</Th>
                <Th right>3M OOS IC</Th>
                <Th right>3M D10–D1</Th>
                <Th right>1M OOS IC</Th>
                <Th right>Sig (3M)</Th>
              </tr>
            </thead>
            <tbody>
              {["levels_controls", "plus_changes", "plus_options"].map((name) => {
                const r = ml.results[name] || {};
                return (
                  <tr key={name} className="border-b border-border/50" data-testid={`bigcap-ml-${name}`}>
                    <Td><span className="font-medium">{SET_LABELS[name]}</span></Td>
                    <Td right><IcCell ic={r.sector_63?.ic} /></Td>
                    <Td right><SpreadCell sp={r.sector_63?.spread} /></Td>
                    <Td right><IcCell ic={r.sector_21?.ic} /></Td>
                    <Td right><SigBadge t={r.sector_63?.spread?.nw_tstat} /></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["changes_over_levels", "Changes over Levels (paired ΔIC)"],
            ["options_over_changes", "Options over Changes (paired ΔIC)"],
          ].map(([k, label]) => {
            const d = ml.delta_ic?.[k];
            return (
              <StatCard key={k} label={label} testId={`bigcap-delta-${k}`}>
                {d ? (
                  <span className="text-sm">
                    <span className="font-mono">{d.mean_delta_ic > 0 ? "+" : ""}{d.mean_delta_ic}</span>{" "}
                    <span className="text-muted-foreground font-mono text-xs">(t={d.nw_tstat}, {d.pct_positive}% dates +)</span>{" "}
                    <SigBadge t={d.nw_tstat} />
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">n/a</span>
                )}
              </StatCard>
            );
          })}
        </div>
        {imp.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-2">Full-model permutation importance (top 10)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="bigcap-ml-importance">
              {imp.map(([f, v]) => (
                <Badge key={f} variant="outline" className="font-mono text-[11px]">
                  {fl(f)}: {Number(v).toFixed(4)}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ---------------- Options Fade ---------------- */

const OptionsFade = ({ block }) => {
  const per = block?.per_signal || {};
  if (!Object.keys(per).length) return null;
  return (
    <Card data-testid="bigcap-options-fade">
      <CardHeader>
        <CardTitle className="text-base">Options Contrarian Fade — within big caps</CardTitle>
        <CardDescription>
          Fade uplift = −long premium: how much better 13F-bullish names do when the options
          tape is NOT already bullish. Negative short premium = options-bullish names fall
          harder (better shorts). Forward sector-relative alpha.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Signal × Options Metric</Th>
                <Th right>1M Long Prem</Th>
                <Th right>3M Long Prem</Th>
                <Th right>3M Short Prem</Th>
                <Th right>Sig (3M L)</Th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(per).map(([sig, entry]) =>
                Object.entries(entry.metrics || {}).map(([met, m]) => {
                  const l21 = m.sector_21?.long_premium;
                  const l63 = m.sector_63?.long_premium;
                  const s63 = m.sector_63?.short_premium;
                  return (
                    <tr key={`${sig}-${met}`} className="border-b border-border/50"
                        data-testid={`bigcap-fade-${sig}-${met}`}>
                      <Td>
                        <span className="font-medium">{entry.label}</span>{" "}
                        <span className="text-muted-foreground">× {m.label}</span>
                      </Td>
                      <Td right>
                        {l21 ? <><AlphaVal value={l21.mean_pct} /> <span className="text-muted-foreground font-mono text-xs">(t={l21.nw_tstat})</span></> : "-"}
                      </Td>
                      <Td right>
                        {l63 ? <><AlphaVal value={l63.mean_pct} /> <span className="text-muted-foreground font-mono text-xs">(t={l63.nw_tstat})</span></> : "-"}
                      </Td>
                      <Td right>
                        {s63 ? <><AlphaVal value={s63.mean_pct} /> <span className="text-muted-foreground font-mono text-xs">(t={s63.nw_tstat})</span></> : "-"}
                      </Td>
                      <Td right><SigBadge t={l63?.nw_tstat} /></Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-muted-foreground mt-2">{block?.note}</div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Vol Conditioning ---------------- */

const VolConditioning = ({ block }) => {
  const entries = Object.entries(block || {});
  if (!entries.length) return null;
  return (
    <Card data-testid="bigcap-vol-conditioning">
      <CardHeader>
        <CardTitle className="text-base">Volatility Structure as Standalone Signal</CardTitle>
        <CardDescription>Big-cap-only ICs and quintile spreads of priced-vol metrics.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entries.map(([met, b]) => (
            <div key={met} className="border rounded-lg p-3" data-testid={`bigcap-vol-${met}`}>
              <div className="font-medium text-sm">{b.label}</div>
              <div className="text-[11px] text-muted-foreground mb-2">{b.rationale}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>1M IC: <IcCell ic={b.sector_21?.ic} /></div>
                <div>3M IC: <IcCell ic={b.sector_63?.ic} /></div>
                <div>1M Q5–Q1: <SpreadCell sp={b.sector_21?.spread} /></div>
                <div>3M Q5–Q1: <SpreadCell sp={b.sector_63?.spread} /></div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Regime x Options (Track B) ---------------- */

const RegimeOptions = ({ block }) => {
  const fade = block?.fade || {};
  const metrics = Object.keys(fade);
  const [met, setMet] = useState(metrics[0]);
  const sa = block?.score_ablation;
  if (!metrics.length && !sa) return null;
  const active = fade[met] || fade[metrics[0]];
  return (
    <Card data-testid="bigcap-regime-options">
      <CardHeader>
        <CardTitle className="text-base">Regime × Options — better regime selection?</CardTitle>
        <CardDescription>
          Within each nowcast regime (options-covered top-250 universe): does picking the
          options-<em>quiet</em> tercile beat the options-<em>hot</em> tercile? Plus a
          walk-forward test: cluster-only score vs options-augmented score.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {metrics.length > 0 && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <ToggleGroup
                options={metrics.map((m) => ({ value: m, label: fade[m].label }))}
                value={met || metrics[0]}
                onChange={setMet}
                testId="bigcap-regime-metric"
              />
              <div className="text-xs text-muted-foreground">
                Universe-wide fade: <AlphaVal value={active?.universe_fade_pct} />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <Th>Regime</Th>
                    <Th right>Quiet ⅓</Th>
                    <Th right>Hot ⅓</Th>
                    <Th right>Fade Uplift</Th>
                    <Th right>Welch t</Th>
                    <Th right>Interaction t</Th>
                  </tr>
                </thead>
                <tbody>
                  {(active?.rows || []).map((r) => (
                    <tr key={r.cluster} className="border-b border-border/50"
                        data-testid={`bigcap-regime-row-${r.cluster}`}>
                      <Td>
                        <span className="font-medium">{r.name}</span>{" "}
                        <Badge variant="outline" className="text-[10px] ml-1">{r.quality}</Badge>
                      </Td>
                      <Td right><AlphaVal value={r.quiet_mean_pct} /></Td>
                      <Td right><AlphaVal value={r.hot_mean_pct} /></Td>
                      <Td right><AlphaVal value={r.fade_uplift_pct} /></Td>
                      <Td right><span className="font-mono text-xs">{r.welch_t ?? "-"}</span></Td>
                      <Td right>
                        <span className="font-mono text-xs">{r.interaction_t ?? "-"}</span>{" "}
                        <SigBadge t={r.interaction_t} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {sa && (
          <div className="border rounded-lg p-3 space-y-2" data-testid="bigcap-regime-ablation">
            <div className="text-sm font-medium">Regime score ablation (walk-forward OOS)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {["regime_baseline", "regime_plus_options"].map((name) => {
                const ev = sa.results?.[name];
                return (
                  <div key={name} className="bg-muted/40 rounded p-2 text-xs">
                    <div className="text-muted-foreground mb-1">{SET_LABELS[name]}</div>
                    <div>IC: <IcCell ic={ev?.ic} /></div>
                    <div>Q5–Q1: <SpreadCell sp={ev?.spread} /></div>
                  </div>
                );
              })}
              <div className="bg-muted/40 rounded p-2 text-xs">
                <div className="text-muted-foreground mb-1">Options uplift (paired ΔIC)</div>
                {sa.options_uplift ? (
                  <div>
                    <span className="font-mono">
                      {sa.options_uplift.mean_delta_ic > 0 ? "+" : ""}{sa.options_uplift.mean_delta_ic}
                    </span>{" "}
                    <span className="text-muted-foreground">(t={sa.options_uplift.nw_tstat})</span>{" "}
                    <SigBadge t={sa.options_uplift.nw_tstat} />
                  </div>
                ) : (
                  <div className="text-muted-foreground">n/a</div>
                )}
                <div className="text-muted-foreground mt-1">{sa.n_oos_dates} OOS dates</div>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground">{sa.note}</div>
          </div>
        )}
        <div className="text-xs text-muted-foreground">{block?.note}</div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Tab ---------------- */

export const BigCapLabTab = ({ signal }) => {
  const { result, job, running, run } = signal;
  const res = result?.result;

  return (
    <div className="space-y-4" data-testid="bigcap-tab">
      <Card>
        <CardHeader>
          <CardTitle>Big Cap Lab — recovering alpha in the efficient segment</CardTitle>
          <CardDescription>
            The super signal found no significant big-cap alpha from 13F levels. This lab tests
            positioning <em>innovations</em>, options-market fades, vol structure, a big-cap-only
            walk-forward model, and options-refined regime selection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RunBar result={result} job={job} running={running} onRun={run} testId="bigcap-runbar" />
          {res && (
            <>
              <PanelSummary panel={res.panel} />
              <div className="text-xs text-muted-foreground border-l-2 border-primary/50 pl-3"
                   data-testid="bigcap-motivation">
                {res.motivation} {res.universe?.note}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {res && (
        <>
          <LevelsVsChanges block={res.levels_vs_changes} />
          <MlAblation ml={res.ml_ablation} />
          <OptionsFade block={res.options_fade} />
          <VolConditioning block={res.vol_conditioning} />
          <RegimeOptions block={res.regime_options} />
        </>
      )}
    </div>
  );
};
